import { useMemo, useState } from "react";
import { getActivityUsage, sortByUsage } from "../lib/activityUsage";
import { normalizeSearch } from "../lib/activityLinks";

export interface ActivityPickerItem {
  code: string;
  label: string;
  sub?: string;
  description?: string;
}

interface Props {
  items: ActivityPickerItem[];
  selectedCode: string | null;
  onSelect: (code: string) => void;
}

/** Picker d'activité : recherche live + slider horizontal 2 lignes (remplissage
 * colonne 1 3 5 / 2 4 6) de cartes montrant CODE + Titre + description publique.
 * Les activités déjà utilisées (usage local) remontent en tête. */
export function ActivityPicker({ items, selectedCode, onSelect }: Props) {
  const [query, setQuery] = useState("");

  // Usage lu une fois au montage — pas besoin de réactivité live pendant la sélection.
  const ordered = useMemo(
    () => sortByUsage(items, getActivityUsage()),
    [items],
  );

  const filtered = useMemo(() => {
    const q = normalizeSearch(query.trim());
    if (!q) return ordered;
    return ordered.filter((a) =>
      normalizeSearch(`${a.code} ${a.label} ${a.description ?? ""}`).includes(
        q,
      ),
    );
  }, [ordered, query]);

  return (
    <div className="am-picker">
      <input
        className="am-search"
        type="text"
        placeholder="Rechercher (code, nom…)"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      {filtered.length === 0 ? (
        <div className="am-empty">
          {items.length === 0
            ? "Aucune activité disponible."
            : "Aucun résultat."}
        </div>
      ) : (
        <div className="am-slider">
          {filtered.map((a) => (
            <button
              key={a.code}
              type="button"
              className={`am-card${selectedCode === a.code ? " selected" : ""}`}
              onClick={() => onSelect(a.code)}
            >
              <span className="code">{a.code}</span>
              <span className="am-card-title">{a.label}</span>
              {a.description && (
                <span className="am-card-desc">{a.description}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
